import { useEffect, useRef, useState } from "react";
import { UploadCloud, Copy, Trash2, ImagePlus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImageUrl } from "@/lib/api";

const fmt = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const s = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${s[i]}`;
};

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media");
      setItems(data.items || []);
    } catch { toast.error("Failed to load media"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch { /* ignore individual failure */ }
    }
    setUploading(false);
    toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    load();
  };

  const onPick = (e) => uploadFiles(Array.from(e.target.files || []));
  const onDrop = (e) => { e.preventDefault(); setDragging(false); uploadFiles(Array.from(e.dataTransfer.files || [])); };

  const copyUrl = async (url) => {
    const full = resolveImageUrl(url);
    try {
      await navigator.clipboard.writeText(full);
      toast.success("URL copied");
    } catch { toast.error("Copy failed"); }
  };

  const remove = async (filename) => {
    if (!window.confirm(`Delete ${filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/media/${filename}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const filtered = items.filter((i) => i.filename.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-8" data-testid="admin-media-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Executive Content Studio</p>
          <h1 className="mt-2 font-heading text-3xl font-light tracking-tight md:text-4xl">Media library.</h1>
          <p className="mt-2 font-body text-slate-500">{items.length} asset{items.length === 1 ? "" : "s"}. Drop images to upload.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="media-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search files"
              className="rounded-full border border-slate-200 bg-white pl-9 pr-4 py-2.5 font-body text-sm outline-none focus:border-royal"
            />
          </div>
          <button
            data-testid="media-upload-btn"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 font-body text-sm font-semibold text-white hover:bg-navy"
          >
            <UploadCloud className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload"}
          </button>
          <input ref={inputRef} type="file" accept="image/*" multiple onChange={onPick} className="hidden" />
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-testid="media-dropzone"
        className={`rounded-[1.5rem] border-2 border-dashed p-6 transition-colors ${dragging ? "border-royal bg-royal/5" : "border-slate-200 bg-white"}`}
      >
        {loading ? (
          <p className="py-8 text-center font-body text-sm text-slate-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="py-14 text-center">
            <ImagePlus className="mx-auto h-10 w-10 text-slate-300" />
            <h3 className="mt-3 font-heading text-lg font-bold">{query ? "No matches" : "No media yet"}</h3>
            <p className="mt-1 font-body text-sm text-slate-500">{query ? "Try a different search." : "Drop images here or click upload."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((it) => (
              <div key={it.filename} data-testid={`media-item-${it.filename}`} className="group overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="relative aspect-video overflow-hidden bg-slate-100">
                  <img src={resolveImageUrl(it.url)} alt={it.filename} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                </div>
                <div className="p-3">
                  <p className="truncate font-body text-xs font-semibold text-navy" title={it.filename}>{it.filename}</p>
                  <p className="mt-0.5 font-body text-[11px] text-slate-500">{fmt(it.size)}</p>
                  <div className="mt-2 flex items-center gap-1">
                    <button onClick={() => copyUrl(it.url)} title="Copy URL" className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 font-body text-[11px] font-semibold text-slate-600 hover:border-royal hover:text-royal">
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                    <button onClick={() => remove(it.filename)} title="Delete" className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white p-1.5 text-slate-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
